<template>
  <div
    ref="$el"
    class="slider"
    :style="style"
  >
    <div
      class="body"
      @click="emit('slider:body:click')"
    >
      <div
        ref="$inner"
        class="inner"
        :style="{ transform }"
      >
        <slot />
      </div>
    </div>
    <div class="footer">
      <UiComponentIcon
        type="chevron-left"
        class="prev"
        :class="{ disable: index === 0 }"
        @click="prev"
      />
      <div
        v-for="n in slideCount"
        :key="n"
        class="bullet"
        :class="{ active: index === n - 1 }"
        @click="setIndex(n - 1)"
      />
      <UiComponentIcon
        type="chevron-right"
        class="next"
        :class="{ disable: index === slideCount - 1 }"
        @click="next"
      />
    </div>
  </div>
</template>

<script setup>
// Emitter
const emit = defineEmits([
  'slider:body:click',
  'slider:index:change',
])

// Refs
const $el = ref(null)
const $inner = ref(null)
const index = ref(0)
const slideCount = ref(0)
const width = ref(510)

// Computed Properties
const style = computed(() => {
  return {
    '--container-width': `${width.value}px`,
    '--slide-count': slideCount.value,
  }
})

const transform = computed(() => {
  return `translateX(-${(100 / slideCount.value) * index.value}%)`
})

// Watchers
watch(() => index.value, (value) => {
  emit('slider:index:change', value)
  nextTick(() => {
    setActiveSlide()
  })
})

// Methods
function next() {
  if (index.value === slideCount.value - 1) {
    return
  }

  index.value += 1
}

function onResize() {
  width.value = $el.value.offsetWidth
}

function prev() {
  if (index.value === 0) {
    return
  }

  index.value -= 1
}

function setActiveSlide() {
  [...$inner.value.children].forEach((slide, i) => {
    if (i === index.value) {
      slide.classList.add('active')
    }
    else {
      slide.classList.remove('active')
    }
  })
}

function setIndex(value) {
  if (index.value === value) {
    return
  }
  index.value = value
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onMounted(() => {
  window.addEventListener('resize', onResize)

  slideCount.value = $inner.value.children.length
  width.value = $el.value.offsetWidth

  onResize()
  setActiveSlide()
})

// Expose
defineExpose({
  setIndex,
})
</script>

<style lang="postcss" scoped>
.slider {
  @apply flex flex-col w-full;

  .body {
    @apply flex flex-row w-full h-auto overflow-x-hidden rounded-4xl shadow-light4;

    .inner {
      @apply flex flex-row h-auto transform transition duration-500 ease-in-out;
      width: calc(var(--container-width) * var(--slide-count));

      .slide {
        width: var(--container-width);
      }
    }
  }

  .footer {
    @apply flex flex-row w-full justify-center items-center mt-10;

    .prev,
    .next {
      @apply font-semibold text-livestorm-blue-700 cursor-pointer transition duration-500 ease-out;

      &.disable {
        @apply text-livestorm-blue-200 cursor-default;
      }
    }

    .prev {
      @apply mr-6;
    }

    .bullet {
      @apply w-3 h-3 ml-2 mr-2 rounded-full bg-livestorm-blue-200 cursor-pointer transition duration-500 ease-out;

      &.active {
        @apply bg-livestorm-blue-700 cursor-default;
      }

      &:first-child {
        @apply ml-0;
      }

      &:last-child {
        @apply mr-0;
      }
    }

    .next {
      @apply ml-6;
    }
  }
}
</style>
